import React from 'react';
import dayjs from 'dayjs';
import { downloadExcel } from '../../utils/excel';
import { categoryList } from './lists';
import { t } from '../../company/labels';

const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

/**
 * Share of Shelf logic: category filter, collapse state, the per-category (or per-date) faces
 * series and the Excel export. The view only renders the chart and controls.
 */
export function useShareOfShelf({ storeChecks, channel, isClientView = false }) {
    const [filter, setFilter] = React.useState('all');
    const [isExpanded, setIsExpanded] = React.useState(true);

    const storeChecksFilterCategories = storeChecks?.flatMap((cat) => cat?.categories?.flatMap((c) => ({ ...c, date: cat?.createdAt })) || []) || [];
    const filterCats = storeChecksFilterCategories?.filter((cat) => cat.category === filter);

    const getCategories = () => {
        if (isClientView && filter === 'all') {
            const storeChecksCategoryMap = storeChecks?.flatMap((storeCheck) => storeCheck?.categories || []) || [];

            return categoryList.map(({ name, value }) => {
                const relevantChecks = storeChecksCategoryMap.filter(
                    (s) => (channel === 'tradicional' && s.category === value) || (channel === 'moderno' && s.category === name)
                );
                return {
                    category: name,
                    faces: avg(relevantChecks.map((s) => s.faces)),
                    totalFaces: avg(relevantChecks.map((s) => s.totalFaces))
                };
            });
        } else if (!isClientView && filter === 'all') {
            const lastStoreCheck = storeChecks?.[storeChecks.length - 1] ?? {};
            const catMap = lastStoreCheck.categories?.map((cat) => ({ category: cat.category, faces: cat.faces, totalFaces: cat.totalFaces })) || [];
            return catMap || [];
        } else if (isClientView) {
            const groupedData = Object.values(
                filterCats.reduce((acc, { date, faces, totalFaces }) => {
                    const formattedDate = dayjs(date).format('DD/MM/YYYY');

                    if (!acc[formattedDate]) {
                        acc[formattedDate] = { date: formattedDate, faces: [], totalFaces: [] };
                    }

                    acc[formattedDate].faces.push(faces);
                    acc[formattedDate].totalFaces.push(totalFaces);

                    return acc;
                }, {})
            ).map(({ date, faces, totalFaces }) => ({
                category: date,
                faces: avg(faces),
                totalFaces: avg(totalFaces)
            }));

            return groupedData;
        } else {
            return filterCats.map(({ date, faces, totalFaces }) => ({ category: dayjs(date).format('DD/MM/YYYY'), faces, totalFaces }));
        }
    };

    /** Chart series: x labels plus Mary vs. competition faces per bar. */
    const getChartSeries = () => {
        const categories = getCategories();
        const labels =
            filter === 'all' && isClientView
                ? categories.map(({ category }) => categoryList.find((c) => c.name === category)?.value)
                : filter === 'all' && !isClientView
                ? categories.map((category) => category?.category)
                : categories.map(({ category }) => category);

        const mary = categories.map(({ faces }) => faces);
        const competencia = categories.map(({ faces, totalFaces }) => Math.abs(totalFaces - faces));
        return { labels, mary, competencia };
    };

    const download = () => {
        const categories = getCategories();

        const ownFaces = t('ownBrandFaces');
        const ownPercent = t('ownBrandFacesPercent');
        const columns = ['Categoria', ownFaces, ownPercent, 'Caras Competencia', 'Porcentaje Caras Competencia'];
        const data = categories.map(({ category, faces, totalFaces }) => {
            const competitionFaces = Math.abs(totalFaces - faces);
            const percent = (val) => (isNaN(val) ? '0.00%' : `${val.toFixed(2)}%`);
            return {
                Categoria: category,
                [ownFaces]: faces || 0,
                [ownPercent]: percent((faces / totalFaces) * 100),
                'Caras Competencia': competitionFaces || 0,
                'Porcentaje Caras Competencia': percent((competitionFaces / totalFaces) * 100)
            };
        });
        downloadExcel(columns, data, isClientView ? 'ShareOfShelfCliente.xlsx' : 'ShareOfShelfPDV.xlsx');
    };

    return { filter, setFilter, isExpanded, setIsExpanded, getChartSeries, download };
}
